import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@arabquiz/ui'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { Modal } from './Modal'
import { ConfirmDialog } from './ConfirmDialog'
import { questionBankKeys, type BankCategory } from './types'

type CategoryManagerProps = {
  open: boolean
  onClose: () => void
}

/**
 * Create / rename / delete the caller's **private** categories. Public
 * categories are listed read-only (admin-managed taxonomy). Deleting a
 * category orphans its items to "uncategorized" server-side (`SetNull`),
 * which the confirm dialog warns about.
 */
export function CategoryManager({ open, onClose }: CategoryManagerProps) {
  const { t } = useTranslation('quiz')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()

  const [newName, setNewName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [pendingDelete, setPendingDelete] = useState<BankCategory | null>(null)

  const categoriesQuery = useQuery({
    queryKey: questionBankKeys.categories(),
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/question-bank/categories', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load categories: ${res.status}`)
      }
      return (await res.json()) as BankCategory[]
    },
    enabled: open,
  })

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: questionBankKeys.categories() })
    void queryClient.invalidateQueries({ queryKey: questionBankKeys.itemsRoot })
  }

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch('/api/v1/question-bank/categories', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      })
      if (!res.ok) throw new Error(`Failed to create category: ${res.status}`)
    },
    onSuccess: () => {
      setNewName('')
      invalidate()
    },
  })

  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const res = await fetch(
        `/api/v1/question-bank/categories/${encodeURIComponent(id)}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        },
      )
      if (!res.ok) throw new Error(`Failed to rename category: ${res.status}`)
    },
    onSuccess: () => {
      setEditingId(null)
      setEditingName('')
      invalidate()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(
        `/api/v1/question-bank/categories/${encodeURIComponent(id)}`,
        { method: 'DELETE', credentials: 'include' },
      )
      if (!res.ok) throw new Error(`Failed to delete category: ${res.status}`)
    },
    onSuccess: () => {
      setPendingDelete(null)
      invalidate()
    },
  })

  const isError =
    createMutation.isError || renameMutation.isError || deleteMutation.isError

  const categories = categoriesQuery.data ?? []

  return (
    <Modal open={open} onClose={onClose} title={t('categoryManagerTitle')}>
      {/* Add a new private category */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          const name = newName.trim()
          if (name.length === 0) return
          createMutation.mutate(name)
        }}
        className="flex items-center gap-2"
      >
        <label htmlFor="category-new-name" className="sr-only">
          {t('categoryNamePlaceholder')}
        </label>
        <Input
          id="category-new-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t('categoryNamePlaceholder')}
          maxLength={100}
        />
        <Button
          type="submit"
          disabled={newName.trim().length === 0 || createMutation.isPending}
          className="shrink-0 gap-2"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span>{t('categoryCreate')}</span>
        </Button>
      </form>

      {isError ? (
        <div
          className="mt-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
          role="alert"
        >
          {t('bankSaveError')}
        </div>
      ) : null}

      <div className="mt-5">
        {categoriesQuery.isLoading ? (
          <p className="text-sm text-brand-600" role="status" aria-live="polite">
            {tCommon('loading')}
          </p>
        ) : categories.length === 0 ? (
          <p className="rounded-xl border border-dashed border-brand-200 bg-white/60 px-4 py-8 text-center text-sm text-brand-600">
            {t('categoryEmpty')}
          </p>
        ) : (
          <ul role="list" className="space-y-2">
            {categories.map((category) => {
              const isOwn = category.scope === 'Private'
              const isEditing = editingId === category.id
              return (
                <li
                  key={category.id}
                  className="flex items-center gap-2 rounded-xl border border-brand-100 bg-white ps-4 pe-2 py-2"
                >
                  {isEditing ? (
                    <>
                      <label htmlFor={`category-edit-${category.id}`} className="sr-only">
                        {t('categoryRename')}
                      </label>
                      <Input
                        id={`category-edit-${category.id}`}
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        maxLength={100}
                        autoFocus
                        className="h-9"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const name = editingName.trim()
                          if (name.length === 0) return
                          renameMutation.mutate({ id: category.id, name })
                        }}
                        disabled={renameMutation.isPending}
                        aria-label={tCommon('save')}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      >
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null)
                          setEditingName('')
                        }}
                        aria-label={tCommon('cancel')}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-brand-900">
                        {category.name}
                      </span>
                      <span className="shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-600 tabular-nums">
                        {t('questionsCount', { count: category.itemCount })}
                      </span>
                      {isOwn ? (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(category.id)
                              setEditingName(category.name)
                            }}
                            aria-label={t('categoryRename')}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                          >
                            <Pencil className="h-4 w-4" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDelete(category)}
                            aria-label={t('categoryDelete')}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </>
                      ) : (
                        <span className="shrink-0 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-semibold text-brand-700">
                          {t('bankScopePublic')}
                        </span>
                      )}
                    </>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('categoryDelete')}
        message={t('categoryDeleteConfirm')}
        confirmPending={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id)
        }}
        onClose={() => setPendingDelete(null)}
      />
    </Modal>
  )
}
